<script setup lang="ts">
  import type { FillLogsChangeItem } from '../model/fill-logs-text';
  import FillLogsValueText from './FillLogsValueText.vue';

  defineProps<{ item: FillLogsChangeItem }>();
  const emit = defineEmits<{ (e: 'show', item: FillLogsChangeItem): void }>();
</script>

<template>
  <button
    type="button"
    class="flex w-full flex-col gap-2.5 border-l-[3px] py-4 pr-6 pl-[21px] text-left hover:bg-gray-09 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--theme-secondary-ink)]"
    :class="item.tone === 'filled' ? 'border-l-change-added-border' : 'border-l-change-fixed-border'"
    :data-key="item.key"
    :data-tone="item.tone"
    data-testid="fill-logs-change-item"
    @click="emit('show', { ...item, label: item.context ? `${item.context} · ${item.label}` : item.label })"
  >
    <span
      class="text-xs/[18px] font-semibold tracking-[.02em] text-gray-07 [overflow-wrap:anywhere]"
      data-testid="fill-logs-change-name"
    >
      <span
        v-if="item.context"
        class="font-medium text-gray-03"
      >{{ item.context }} · </span>{{ item.label }}
    </span>

    <span
      v-if="item.cells.length > 0"
      class="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-x-4 gap-y-1.5"
    >
      <span
        v-for="cell in item.cells"
        :key="cell.columnIndex"
        class="flex min-w-0 flex-col gap-0.5"
        data-testid="fill-logs-change-cell"
      >
        <span class="text-xs/4 font-semibold text-gray-03 [overflow-wrap:anywhere]">{{ cell.label }}</span>
        <span class="text-sm/5 text-gray-07">
          <FillLogsValueText :value="cell.value" />
        </span>
      </span>
    </span>

    <template v-else>
      <span
        v-if="item.before"
        class="flex max-w-[70ch] flex-col gap-0.5"
        data-testid="fill-logs-change-before"
      >
        <span class="text-xs/4 text-gray-03">Antes</span>
        <span class="text-[13px]/5 text-gray-03">
          <FillLogsValueText :value="item.before" />
          <span class="ml-1.5 text-xs">{{ item.before.origin }}</span>
        </span>
      </span>
      <span
        v-if="item.now"
        class="flex max-w-[70ch] flex-col gap-0.5"
        data-testid="fill-logs-change-now"
      >
        <span class="text-xs/4 text-gray-03">Agora</span>
        <span class="text-sm/[22px] text-gray-07">
          <FillLogsValueText :value="item.now" />
        </span>
      </span>
    </template>
    <span class="sr-only">. Ver no registro.</span>
  </button>
</template>
